import React from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';

import { computeText } from '@/locales/utils';
import { useGuestGroupFieldOptions } from '@/hooks/hierarchization/useGuestGroupFieldOptions';
import { GuestGroupsByDepthKey } from '@/pages/Brand/Groups/types';
import { Select } from '@/components/form/Select';
import { Input } from '@/components/form/Input';
import FieldInfo from '@/components/molecules/Channel/FieldInfo';
import {
    getAccountPaymentTypeOptions,
    getRedirectionLink,
    PRICING_CONFIGURATION_LINKS,
} from '@/utils/factories/hierarchization/hierarchization.utils';
import { FlexContainer } from './UpdateInfoForm.style';
import { MultiSelect } from '@/components/form/MultiSelect';

type UpdateCustomerFormProps = {
    brandId: number;
};

const UpdateInfoForm: React.FunctionComponent<React.PropsWithChildren<UpdateCustomerFormProps>> = ({ brandId }) => {
    const intl = useIntl();
    const history = useHistory();

    const {
        groups,
        sections,
        subSections,
        isFetchingGroups,
        isFetchingSections,
        isFetchingSubSections,
        disableSection,
        disableSubSection,
        displayInfo,
    } = useGuestGroupFieldOptions({ brandId });

    const handleRedirectionLinkClick = (link: string, depth?: GuestGroupsByDepthKey) => () => {
        if (brandId) {
            history.push(getRedirectionLink(link, brandId, { depth }));
        }
    };

    return (
        <FlexContainer>
            <Select
                labelId="customer.student.info.account.type"
                name={'accountPaymentType'}
                rules={{
                    required: computeText(intl, 'field.required'),
                }}
                options={getAccountPaymentTypeOptions(intl)}
            />
            <div>
                <MultiSelect
                    isMulti={false}
                    placeholder={''}
                    isClearable={true}
                    labelId="customer.student.info.group"
                    name={'groupId'}
                    rules={{
                        required: computeText(intl, 'field.required'),
                    }}
                    options={groups}
                    isLoading={isFetchingGroups}
                />
                {displayInfo && (
                    <FieldInfo
                        message={computeText(intl, 'customer.form.groupInfo')}
                        handleRedirectionLinkClick={handleRedirectionLinkClick(
                            PRICING_CONFIGURATION_LINKS.GUEST_GROUPS,
                            GuestGroupsByDepthKey.GROUPS,
                        )}
                    />
                )}
            </div>
            <div>
                <MultiSelect
                    isMulti={false}
                    placeholder={''}
                    isClearable={true}
                    labelId="customer.student.info.section"
                    name="sectionId"
                    options={sections}
                    isLoading={isFetchingSections}
                    isDisabled={disableSection}
                />
                {displayInfo && (
                    <FieldInfo
                        message={computeText(intl, 'customer.form.sectionInfo')}
                        handleRedirectionLinkClick={handleRedirectionLinkClick(
                            PRICING_CONFIGURATION_LINKS.GUEST_GROUPS,
                            GuestGroupsByDepthKey.SECTIONS,
                        )}
                    />
                )}
            </div>
            <div>
                <MultiSelect
                    isMulti={false}
                    placeholder={''}
                    isClearable={true}
                    labelId="customer.student.info.sub.section"
                    name="subSectionId"
                    options={subSections}
                    isLoading={isFetchingSubSections}
                    isDisabled={disableSubSection}
                />
                {displayInfo && (
                    <FieldInfo
                        message={computeText(intl, 'customer.form.subSectionInfo')}
                        handleRedirectionLinkClick={handleRedirectionLinkClick(
                            PRICING_CONFIGURATION_LINKS.GUEST_GROUPS,
                            GuestGroupsByDepthKey.SUB_SECTIONS,
                        )}
                    />
                )}
            </div>
            <Input labelId="customer.student.info.pricing.code" name={'tariffCode'} />
            <Input labelId="customer.student.info.student.number" name={'studentNumber'} />
            <Input labelId="customer.student.info.badge.number" name={'badgeNumber'} />
        </FlexContainer>
    );
};

export default UpdateInfoForm;
